import { Box, Button, Heading, Text, VStack } from '@chakra-ui/react';
import React from 'react';

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box
          height="100vh"
          display="flex"
          alignItems="center"
          justifyContent="center"
          bg="gray.50"
        >
          <VStack spacing={4} textAlign="center" maxW="md">
            <Heading size="lg" color="red.500">
              Something went wrong
            </Heading>
            <Text color="gray.600">
              The application encountered an error. This might be due to:
            </Text>
            <VStack spacing={2} textAlign="left" fontSize="sm">
              <Text>• Missing or invalid configuration</Text>
              <Text>• Network connectivity issues</Text>
              <Text>• Browser compatibility problems</Text>
            </VStack>
            <Button colorScheme="blue" onClick={() => window.location.reload()}>
              Reload Page
            </Button>
            {process.env.NODE_ENV === 'development' && (
              <Text fontSize="xs" color="gray.500" mt={4}>
                {this.state.error?.message}
              </Text>
            )}
          </VStack>
        </Box>
      );
    }

    return this.props.children;
  }
}
